import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Loader2, Eye, EyeOff, CheckCircle, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const passwordRules = [
  { key: "min", label: "Mínimo de 8 caracteres", test: (p: string) => p.length >= 8 },
  { key: "upper", label: "Uma letra maiúscula", test: (p: string) => /[A-Z]/.test(p) },
  { key: "lower", label: "Uma letra minúscula", test: (p: string) => /[a-z]/.test(p) },
  { key: "number", label: "Um número", test: (p: string) => /\d/.test(p) },
  { key: "special", label: "Um caractere especial (!@#$%...)", test: (p: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p) },
];

const translateResetError = (msg: string): string => {
  const map: Record<string, string> = {
    "New password should be different from the old password.": "A nova senha deve ser diferente da senha atual.",
    "Auth session missing!": "Sessão expirada. Solicite um novo link de recuperação.",
    "Password should be at least 6 characters": "A senha deve ter no mínimo 6 caracteres.",
    "Password should be at least 6 characters.": "A senha deve ter no mínimo 6 caracteres.",
  };

  // Handle known weak/breached password patterns
  if (/weak|breached|leaked|pwned|common/i.test(msg)) {
    return "Esta senha é muito fraca ou já foi exposta em vazamentos de dados. Escolha uma senha mais segura.";
  }

  return map[msg] || msg;
};

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [isRecovery, setIsRecovery] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const ruleResults = useMemo(() => passwordRules.map((r) => ({ ...r, passed: r.test(password) })), [password]);
  const allRulesPassed = ruleResults.every((r) => r.passed);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsRecovery(true);
      }
    });

    const hash = window.location.hash;
    if (hash.includes("type=recovery") || hash.includes("type=magiclink")) {
      setIsRecovery(true);
    }

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!allRulesPassed) {
      setError("A senha não atende todos os requisitos");
      return;
    }

    if (!passwordsMatch) {
      setError("As senhas não coincidem");
      return;
    }

    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    if (updateError) {
      setError(translateResetError(updateError.message));
      return;
    }

    setSuccess(true);
    toast({ title: "Senha redefinida!", description: "Sua senha foi alterada com sucesso." });

    setTimeout(() => navigate("/dashboard", { replace: true }), 2000);
  };

  if (!isRecovery) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-8 md:p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="bg-card rounded-2xl p-6 shadow-card border border-border text-center space-y-4">
            <Lock className="w-10 h-10 text-muted-foreground mx-auto" />
            <h1 className="text-xl font-bold text-foreground">Link inválido ou expirado</h1>
            <p className="text-muted-foreground text-sm">
              Este link de recuperação não é válido ou já expirou. Solicite um novo link.
            </p>
            <Link to="/forgot-password">
              <Button className="w-full gradient-primary text-primary-foreground">Solicitar novo link</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-8 md:p-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
          <div className="bg-card rounded-2xl p-6 shadow-card border border-border text-center space-y-4">
            <CheckCircle className="w-12 h-12 text-primary mx-auto" />
            <h1 className="text-xl font-bold text-foreground">Senha redefinida!</h1>
            <p className="text-muted-foreground text-sm">Redirecionando para o painel...</p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-8 md:p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-6 md:mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 md:w-14 md:h-14 rounded-2xl gradient-primary mb-4">
            <Lock className="w-8 h-8 md:w-7 md:h-7 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Nova Senha</h1>
          <p className="text-base md:text-sm text-muted-foreground mt-1">Defina sua nova senha de acesso</p>
        </div>

        <div className="bg-card rounded-2xl p-6 shadow-card border border-border">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-base md:text-sm">Nova senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPass ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-[52px] text-base md:h-10 md:text-sm"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPass(!showPass)}>
                  {showPass ? <EyeOff className="w-5 h-5 md:w-4 md:h-4" /> : <Eye className="w-5 h-5 md:w-4 md:h-4" />}
                </button>
              </div>
            </div>

            {password.length > 0 && (
              <div className="rounded-xl border border-border bg-muted/50 p-3 space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Requisitos de senha segura:</p>
                {ruleResults.map((r) => (
                  <div key={r.key} className="flex items-center gap-2 text-xs">
                    {r.passed ? (
                      <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                    ) : (
                      <X className="w-3.5 h-3.5 text-destructive shrink-0" />
                    )}
                    <span className={r.passed ? "text-foreground" : "text-muted-foreground"}>{r.label}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-base md:text-sm">Confirmar senha</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-[52px] text-base md:h-10 md:text-sm"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowConfirm(!showConfirm)}>
                  {showConfirm ? <EyeOff className="w-5 h-5 md:w-4 md:h-4" /> : <Eye className="w-5 h-5 md:w-4 md:h-4" />}
                </button>
              </div>
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="text-xs text-destructive">As senhas não coincidem</p>
              )}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button
              type="submit"
              disabled={submitting || !allRulesPassed || !passwordsMatch}
              className="w-full h-[52px] md:h-10 text-base md:text-sm font-semibold gradient-primary text-primary-foreground hover:opacity-90 transition-opacity"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
              Redefinir senha
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
